/**
 * SubsanarVoucherModal — Modal para regularizar una reserva con
 * voucher_pendiente_subsanar = TRUE.
 *
 * Estilo: teal, consistente con ReservaFinancieroModal. NO reutiliza el modal
 * de booking (ese es para editar la reserva; este es específicamente para
 * resolver el vacío de voucher).
 *
 * 3 opciones:
 *   A) Subir voucher válido (admin/superadmin) → aprueba automático + desbloquea
 *   B) Reclasificar pago como efectivo (admin/superadmin)
 *   C) Anular el pago sin voucher (admin/superadmin) → restaura el saldo
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Chip, Alert, CircularProgress, Divider, TextField, MenuItem, Select,
  FormControl, InputLabel, IconButton, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

// Endpoints protegidos con require_role usan token Firebase real (NO el de
// localStorage). Este helper obtiene el ID token del usuario logueado.
async function getAuthHeaders(json = true) {
  const h = await getFirebaseAuthHeaders();
  if (!json) delete h['Content-Type'];  // multipart lo pone el navegador
  return h;
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}
const fmt = (n) => new Intl.NumberFormat('es-CO').format(Math.round(Number(n) || 0));
const fmtCol = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'America/Bogota',
    });
  } catch { return iso; }
};

export default function SubsanarVoucherModal({ reservaId, onClose, onSubsanado }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const role = getUserRole();
  // Todas las opciones habilitadas para admin y superadmin.
  const puedeSubsanar = role === 'admin' || role === 'superadmin';

  const cargar = async () => {
    if (!reservaId) return;
    setLoading(true); setError(null);
    try {
      const headers = await getAuthHeaders();
      const r = await fetch(`${API_BASE}/turismo/vouchers/subsanar/${reservaId}/diagnostico`, { headers });
      if (!r.ok) throw new Error('Error al cargar');
      setData(await r.json());
    } catch (e) { setError(String(e.message || e)); }
    setLoading(false);
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [reservaId]);

  if (!reservaId) return null;

  const r = data?.reserva;
  const creador = data?.creador;
  const pagosSinVoucher = data?.pagos_sin_voucher || [];
  const pagoProblematico = pagosSinVoucher[0]; // asumimos 1 a la vez para simplificar

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      {/* Header teal */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
        color: '#fff', p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <WarningAmberIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
            Subsanar voucher · {r?.codigo_reserva || 'Cargando…'}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
            {r?.nombre_cliente || '—'} · {r?.agencia_nombre || '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && r && (
          <>
            {/* Info de creación */}
            <Box sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, mb: 2, p: 1.5,
              bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  Reserva creada
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>
                  {fmtCol(r.created_at)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  Creada por
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 15, color: '#0891b2' }} />
                  {creador?.nombre || creador?.email || '(no registrado)'}
                  {creador?.origen && (
                    <Chip label={creador.origen} size="small"
                      sx={{ fontSize: 9, height: 16, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Situación detectada */}
            <Alert severity="error" icon={<WarningAmberIcon />}
              sx={{ mb: 2, border: '2px solid #dc2626', bgcolor: '#fef2f2' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Situación detectada
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: '#7f1d1d' }}>
                {pagosSinVoucher.length === 0 ? (
                  'Esta reserva está marcada como pendiente, pero no se detectó ningún pago sin voucher. Verifica manualmente.'
                ) : (
                  <>
                    Esta reserva registró {pagosSinVoucher.length} pago(s) con método no-efectivo pero SIN voucher válido asociado:
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {pagosSinVoucher.map(p => (
                        <li key={p.id}>
                          <b>{p.metodo_pago}</b> por <b>${fmt(p.monto)}</b> — aplicado por <i>{p.aplicado_por}</i> el {fmtCol(p.fecha_col)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Typography>
            </Alert>

            {/* Estado financiero */}
            <Box sx={{ p: 1.5, mb: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                Estado financiero
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Precio: $${fmt(r.precio_total)}`} size="small" sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600 }} />
                {r.comision_anticipada > 0 && (
                  <Chip label={`Com. anticipada: $${fmt(r.comision_anticipada)}`} size="small" sx={{ bgcolor: '#fefce8', color: '#a16207', fontWeight: 600 }} />
                )}
                <Chip label={`Saldo pendiente: $${fmt(r.saldo_pendiente)}`} size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }} />
                <Chip label={r.estado} size="small" sx={{ bgcolor: '#e2e8f0', color: '#334155', fontWeight: 600 }} />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                Cómo subsanar
              </Typography>
            </Divider>

            {/* Opción A — Subir voucher */}
            <OpcionSubirVoucher
              reservaId={reservaId}
              pago={pagoProblematico}
              cuentasDestino={data?.cuentas_destino_disponibles || []}
              onDone={() => { onSubsanado?.(); onClose(); }}
            />

            {/* Opción B y C — admin y superadmin */}
            {puedeSubsanar && pagoProblematico && (
              <>
                <Divider sx={{ my: 2 }} />
                <OpcionReclasificarEfectivo
                  reservaId={reservaId}
                  pago={pagoProblematico}
                  cuentasDestino={data?.cuentas_destino_disponibles || []}
                  onDone={() => { onSubsanado?.(); onClose(); }}
                />
                <Divider sx={{ my: 2 }} />
                <OpcionAnularPago
                  reservaId={reservaId}
                  pago={pagoProblematico}
                  onDone={() => { onSubsanado?.(); onClose(); }}
                />
              </>
            )}

            {!puedeSubsanar && pagoProblematico && (
              <Alert severity="info" sx={{ mt: 2, fontSize: 11.5 }}>
                Opciones adicionales (reclasificar como efectivo o anular el pago) están limitadas a admin/superadmin.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Opción A: Subir voucher ─────────────────────────────────── */
function OpcionSubirVoucher({ reservaId, pago, cuentasDestino, onDone }) {
  const [archivo, setArchivo] = useState(null);
  const [referencia, setReferencia] = useState('');
  const [cuentaCorrecta, setCuentaCorrecta] = useState('');
  const [validacion, setValidacion] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState(null);

  const validar = async (file) => {
    if (!file || !pago) return;
    setError(null); setValidacion(null);
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('empresa_id', '1');
    fd.append('metodo_pago', pago.metodo_pago);
    fd.append('monto_esperado', String(pago.monto));
    if (pago.cuenta_destino_id) fd.append('cuenta_destino_esperada_id', String(pago.cuenta_destino_id));
    try {
      const headers = await getAuthHeaders(false);
      const r = await fetch(`${API_BASE}/turismo/vouchers/validar-preview`, {
        method: 'POST', headers, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Error validando');
      setValidacion(d);
    } catch (e) { setError(String(e.message || e)); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setArchivo(f || null);
    if (f) validar(f);
  };

  const subir = async () => {
    if (!archivo || !pago) return;
    setSubiendo(true); setError(null);
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('metodo_pago', pago.metodo_pago);
    fd.append('monto_esperado', String(pago.monto));
    if (pago.cuenta_destino_id) fd.append('cuenta_destino_esperada_id', String(pago.cuenta_destino_id));
    if (cuentaCorrecta) fd.append('cuenta_destino_id_correcta', String(cuentaCorrecta));
    if (referencia) fd.append('referencia', referencia);
    try {
      const headers = await getAuthHeaders(false);
      const r = await fetch(`${API_BASE}/turismo/vouchers/subsanar/${reservaId}/subir-voucher`, {
        method: 'POST', headers, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail));
      onDone?.();
    } catch (e) { setError(String(e.message || e)); }
    setSubiendo(false);
  };

  const bloqueado = validacion?.bloqueantes?.length > 0;
  const tieneWarningCuenta = validacion?.warnings?.some(w => w.toLowerCase().includes('cuenta'));

  return (
    <Box sx={{ p: 2, bgcolor: '#f0fdfa', borderRadius: 2, borderLeft: '4px solid #0d9488' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488', mb: 0.5 }}>
        Opción A — Subir voucher válido
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', mb: 1.5 }}>
        Sube el comprobante real del pago. El sistema valida (MD5, monto, cuenta) y si pasa, se aprueba automáticamente y desbloquea la reserva.
      </Typography>

      <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}
        sx={{ textTransform: 'none', borderColor: '#0d9488', color: '#0d9488' }}>
        {archivo ? archivo.name : 'Elegir archivo del voucher'}
        <input type="file" accept="image/*,application/pdf" hidden onChange={handleFile} />
      </Button>

      {validacion && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            Resultado de validación:
          </Typography>
          {bloqueado && (
            <Alert severity="error" sx={{ fontSize: 11.5, mb: 1 }}>
              {validacion.bloqueantes.map((b, i) => <div key={i}>❌ {b}</div>)}
              {validacion.duplicado_de && (
                <Typography sx={{ fontSize: 11, mt: 0.5, fontStyle: 'italic' }}>
                  Ya fue usado en: <b>{validacion.duplicado_de.codigo_reserva || `reserva #${validacion.duplicado_de.reservacion_id}`}</b>
                </Typography>
              )}
            </Alert>
          )}
          {!bloqueado && validacion.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ fontSize: 11.5, mb: 1 }}>
              {validacion.warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
            </Alert>
          )}
          {!bloqueado && validacion.warnings?.length === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: 11.5, mb: 1 }}>
              Voucher válido, listo para aprobar.
            </Alert>
          )}
          {validacion.datos_vision && Object.keys(validacion.datos_vision).length > 0 && (
            <Box sx={{ fontSize: 10.5, color: '#475569', mt: 1 }}>
              <b>Datos extraídos:</b> {' '}
              {validacion.datos_vision.monto && `Monto $${fmt(validacion.datos_vision.monto)} · `}
              {validacion.datos_vision.referencia && `Ref: ${validacion.datos_vision.referencia} · `}
              {validacion.datos_vision.cuenta_destino_nombre && `→ ${validacion.datos_vision.cuenta_destino_nombre}`}
            </Box>
          )}
        </Box>
      )}

      {tieneWarningCuenta && (
        <FormControl size="small" sx={{ mt: 1.5, minWidth: 260 }}>
          <InputLabel>Corregir cuenta destino del pago</InputLabel>
          <Select label="Corregir cuenta destino del pago"
            value={cuentaCorrecta} onChange={e => setCuentaCorrecta(e.target.value)}>
            <MenuItem value=""><em>Mantener la actual</em></MenuItem>
            {cuentasDestino.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.nombre} ({c.tipo})</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <TextField size="small" fullWidth label="Referencia (opcional)"
        value={referencia} onChange={e => setReferencia(e.target.value)}
        sx={{ mt: 1.5 }} />

      {error && <Alert severity="error" sx={{ mt: 1, fontSize: 11.5 }}>{error}</Alert>}

      <Button variant="contained" onClick={subir}
        disabled={!archivo || bloqueado || subiendo}
        sx={{ mt: 1.5, background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)' } }}>
        {subiendo ? 'Subiendo…' : 'Aprobar voucher y desbloquear reserva'}
      </Button>
    </Box>
  );
}

/* ─── Opción B: Reclasificar como efectivo ─────────────────────── */
function OpcionReclasificarEfectivo({ reservaId, pago, cuentasDestino, onDone }) {
  const [motivo, setMotivo] = useState('');
  const [cuentaId, setCuentaId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!motivo.trim()) { setError('El motivo es obligatorio'); return; }
    setSaving(true); setError(null);
    try {
      const headers = await getAuthHeaders();
      const r = await fetch(`${API_BASE}/turismo/vouchers/subsanar/${reservaId}/reclasificar-efectivo`, {
        method: 'POST', headers,
        body: JSON.stringify({ pago_id: pago.id, motivo: motivo.trim(),
                               cuenta_destino_id: cuentaId ? Number(cuentaId) : null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Error');
      onDone?.();
    } catch (e) { setError(String(e.message || e)); }
    setSaving(false);
  };

  const cuentasEfectivo = cuentasDestino.filter(c => (c.tipo || '').toLowerCase() === 'efectivo');

  return (
    <Box sx={{ p: 2, bgcolor: '#fefce8', borderRadius: 2, borderLeft: '4px solid #ca8a04' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#a16207', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <PaymentsIcon sx={{ fontSize: 16 }} />
        Opción B — Reclasificar pago como efectivo
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', mb: 1.5 }}>
        Si el pago realmente fue en efectivo (y no requería voucher), reclasifícalo. La reserva se desbloquea. Solo superadmin.
      </Typography>

      {cuentasEfectivo.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 240, mb: 1.5 }}>
          <InputLabel>Cuenta destino efectivo</InputLabel>
          <Select label="Cuenta destino efectivo" value={cuentaId} onChange={e => setCuentaId(e.target.value)}>
            <MenuItem value=""><em>Sin cambio</em></MenuItem>
            {cuentasEfectivo.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      <TextField size="small" fullWidth multiline rows={2}
        label="Motivo (obligatorio, queda en auditoría)"
        value={motivo} onChange={e => setMotivo(e.target.value)} />

      {error && <Alert severity="error" sx={{ mt: 1, fontSize: 11.5 }}>{error}</Alert>}

      <Button variant="outlined" onClick={submit} disabled={!motivo.trim() || saving}
        sx={{ mt: 1.5, borderColor: '#ca8a04', color: '#a16207', textTransform: 'none' }}>
        {saving ? 'Guardando…' : 'Reclasificar como efectivo'}
      </Button>
    </Box>
  );
}

/* ─── Opción C: Anular pago ─────────────────────────────────────── */
function OpcionAnularPago({ reservaId, pago, onDone }) {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!motivo.trim()) { setError('El motivo es obligatorio'); return; }
    if (!window.confirm(`¿Anular el pago de $${fmt(pago.monto)} de esta reserva? El saldo pendiente se restaurará por ese monto.`)) return;
    setSaving(true); setError(null);
    try {
      const headers = await getAuthHeaders();
      const r = await fetch(`${API_BASE}/turismo/vouchers/subsanar/${reservaId}/anular-pago`, {
        method: 'POST', headers,
        body: JSON.stringify({ pago_id: pago.id, motivo: motivo.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Error');
      onDone?.();
    } catch (e) { setError(String(e.message || e)); }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2, borderLeft: '4px solid #dc2626' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#991b1b', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DeleteIcon sx={{ fontSize: 16 }} />
        Opción C — Anular el pago (nunca ocurrió)
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', mb: 1.5 }}>
        Si el pago nunca se realizó (fue error de captura), anúlalo. La reserva se desbloquea y el saldo pendiente se restaura por ${fmt(pago.monto)}. Solo superadmin.
      </Typography>

      <TextField size="small" fullWidth multiline rows={2}
        label="Motivo de la anulación (obligatorio)"
        value={motivo} onChange={e => setMotivo(e.target.value)} />

      {error && <Alert severity="error" sx={{ mt: 1, fontSize: 11.5 }}>{error}</Alert>}

      <Button variant="outlined" color="error" onClick={submit} disabled={!motivo.trim() || saving}
        sx={{ mt: 1.5, textTransform: 'none' }}>
        {saving ? 'Anulando…' : `Anular pago de $${fmt(pago.monto)}`}
      </Button>
    </Box>
  );
}
